import type { ProposalExtraItemType, ProposalProductItemType } from '@econtractor/types';
import vconst from 'utils/helpers/structs/vconst';
import { safeFloatParse, safeIntParse } from 'utils/helpers/safeParsing.js';

export interface ObjectDocumentIncludeFlags {
    includeMaterials: boolean;
    includeTeamExpends: boolean;
    includeOtherExpends: boolean;
}

export interface ObjectDocumentAppendItems {
    products: ProposalProductItemType[];
    additionalItems: ProposalExtraItemType[];
}

const DEFAULT_FLAGS: ObjectDocumentIncludeFlags = {
    includeMaterials: false,
    includeTeamExpends: false,
    includeOtherExpends: false,
};

const asRecord = (value: unknown): Record<string, unknown> | null =>
    value && typeof value === 'object' ? (value as Record<string, unknown>) : null;

const pickNumber = (row: Record<string, unknown>, ...keys: string[]): number => {
    for (const key of keys) {
        if (row[key] != null && row[key] !== '') return safeFloatParse(row[key]);
    }
    return 0;
};

const pickText = (row: Record<string, unknown>, ...keys: string[]): string => {
    for (const key of keys) {
        const value = String(row[key] ?? '').trim();
        if (value) return value;
    }
    return '';
};

const dimensionFields = (row: Record<string, unknown>) => {
    const dimensionName = pickText(row, 'dimensionName', 'dimension_name');
    const dimensionI18nKey = pickText(row, 'dimensionI18nKey', 'dimension_i18n_key');
    return {
        ...(dimensionName ? { dimensionName } : {}),
        ...(dimensionI18nKey ? { dimensionI18nKey } : {}),
    };
};

const mapMaterialsToProducts = (objectData: unknown): ProposalProductItemType[] => {
    const root = asRecord(objectData);
    const operations = Array.isArray(root?.operations) ? root.operations : [];
    const stamp = Date.now();

    return operations
        .map((item, index) => {
            const row = asRecord(item);
            if (!row) return null;
            const operStatus = pickNumber(row, 'operStatus', 'oper_status');
            const itemCount = pickNumber(row, 'operCount', 'oper_count');
            const productId = safeIntParse(pickNumber(row, 'productId', 'product_id'));
            if (operStatus !== vconst.operStatApproved || itemCount <= 0 || !productId) return null;

            const price = pickNumber(row, 'operPrice', 'oper_price');
            const priceRate = pickNumber(row, 'operPriceRate', 'oper_price_rate');
            const priceSale = pickNumber(row, 'operPriceSale', 'oper_price_sale') || price;
            const priceSaleRate = pickNumber(row, 'operPriceSaleRate', 'oper_price_sale_rate') || priceRate;
            const prodName = pickText(row, 'prodName', 'prod_name') || `#${productId}`;

            return {
                rowId: `obj-mat-${stamp}-${index}-${productId}`,
                sourceType: 'product' as const,
                sourceId: productId,
                sourceName: prodName,
                productId,
                itemCount,
                price,
                priceRate,
                priceSale,
                priceSaleRate,
                prodName,
                prodArticle: pickText(row, 'prodArticle', 'prod_article'),
                vendorName: pickText(row, 'vendorName', 'vendor_name'),
                dimensionName: pickText(row, 'dimensionName', 'dimension_name'),
                dimensionI18nKey: pickText(row, 'dimensionI18nKey', 'dimension_i18n_key'),
            };
        })
        .filter(Boolean) as ProposalProductItemType[];
};

const mapTeamExpendsToAdditional = (objectData: unknown): ProposalExtraItemType[] => {
    const root = asRecord(objectData);
    const expendTeams = Array.isArray(root?.expendTeams) ? root.expendTeams : [];
    const stamp = Date.now();

    return expendTeams
        .map((item, index) => {
            const row = asRecord(item);
            if (!row) return null;
            const status = pickNumber(row, 'income_status', 'incomeStatus');
            if (status !== vconst.incomeStatActive) return null;

            const count = Math.abs(pickNumber(row, 'income_count', 'incomeCount')) || 1;
            const unitSale = Math.abs(pickNumber(row, 'income_price_sale', 'incomePriceSale'));
            const unitSaleRate = Math.abs(pickNumber(row, 'income_price_sale_rate', 'incomePriceSaleRate'));
            const unitCost = Math.abs(pickNumber(row, 'income_price', 'incomePrice'));
            const unitCostRate = Math.abs(pickNumber(row, 'income_price_rate', 'incomePriceRate'));
            const totalCost = Math.abs(pickNumber(row, 'income_amount', 'incomeAmount'));
            const totalCostRate = Math.abs(pickNumber(row, 'income_amount_rate', 'incomeAmountRate'));
            // price = cost, priceSale = sale (як у товарів)
            const price = unitCost > 0 ? unitCost : totalCost;
            const priceRate = unitCostRate > 0 ? unitCostRate : totalCostRate;
            const priceSale = unitSale > 0 ? unitSale : price;
            const priceSaleRate = unitSaleRate > 0 ? unitSaleRate : priceRate;
            if (priceSale <= 0 && price <= 0) return null;

            const serviceName = pickText(row, 'income_name', 'incomeName');
            const teamName = pickText(row, 'team_name', 'teamName') || `Team #${index + 1}`;
            const serviceId = pickNumber(row, 'service_id', 'serviceId');
            return {
                rowId: `obj-team-${stamp}-${index}`,
                itemName: serviceName || teamName,
                itemCount: count,
                price,
                priceRate,
                priceSale,
                priceSaleRate,
                ...(serviceId > 0 ? { serviceId } : {}),
                ...dimensionFields(row),
            };
        })
        .filter(Boolean) as ProposalExtraItemType[];
};

const mapOtherExpendsToAdditional = (objectData: unknown): ProposalExtraItemType[] => {
    const root = asRecord(objectData);
    const expends = Array.isArray(root?.expends) ? root.expends : [];
    const stamp = Date.now();

    return expends
        .map((item, index) => {
            const row = asRecord(item);
            if (!row) return null;
            const status = pickNumber(row, 'expendStatus', 'expend_status');
            if (status !== vconst.incomeStatActive) return null;

            const amount = pickNumber(row, 'expendAmount', 'expend_amount');
            // Only expenses (negative amounts), not incomes.
            if (amount >= 0) return null;

            const price = Math.abs(amount);
            if (price <= 0) return null;

            const itemName =
                pickText(row, 'expendName', 'expend_name') ||
                pickText(row, 'expendDescription', 'expend_description') ||
                `Expend #${index + 1}`;

            return {
                rowId: `obj-expend-${stamp}-${index}`,
                asOtherExpense: true,
                itemName,
                itemCount: 1,
                price,
                priceRate: Math.abs(pickNumber(row, 'expendAmountRate', 'expend_amount_rate')),
            };
        })
        .filter(Boolean) as ProposalExtraItemType[];
};

/** Build products/additional items from object card data according to include switches. */
export const buildObjectDocumentAppendItems = (
    objectData: unknown,
    flags: Partial<ObjectDocumentIncludeFlags> | null | undefined,
): ObjectDocumentAppendItems => {
    const resolved = { ...DEFAULT_FLAGS, ...(flags || {}) };
    return {
        products: resolved.includeMaterials ? mapMaterialsToProducts(objectData) : [],
        additionalItems: [
            ...(resolved.includeTeamExpends ? mapTeamExpendsToAdditional(objectData) : []),
            ...(resolved.includeOtherExpends ? mapOtherExpendsToAdditional(objectData) : []),
        ],
    };
};

export const hasObjectDocumentAppendItems = (items: ObjectDocumentAppendItems | null | undefined): boolean =>
    Boolean((items?.products || []).length || (items?.additionalItems || []).length);
